(() => {
  /*
   * <product-sticky-bar>
   *
   * The white strip that appears once the product form has been scrolled past,
   * showing the product name, price and a "Select size" button. "Select size"
   * opens a popup with the available sizes and the "What's my size" guide.
   *
   * Deliberately does NOT talk to the cart itself. Picking a size drives the real
   * product form's own option selector, and the add button clicks the form's own
   * add-to-cart button. The theme therefore keeps ownership of the cart request,
   * the cart drawer and error handling, and this component cannot drift out of
   * sync with it. If the form or its selector cannot be found, the shopper is sent
   * to the form rather than being sold a size we are not certain about.
   */

  /* Watched to decide when the bar appears, first match wins. The add-to-cart
     button is the truest "product info is gone" signal; the form is the fallback. */
  const SCROLL_TARGET_SELECTORS = ['[data-add-to-cart]', '[data-product-form]'];

  const PRODUCT_FORM_SELECTOR = '[data-product-form]';
  const ADD_TO_CART_SELECTOR = '[data-add-to-cart]';
  const OPTION_SELECTOR = '[data-single-option-selector]';
  const BODY_OPEN_CLASS = 'size-popup-is-open';
  const SIZE_GUIDE_MIRROR_SELECTOR = 'kleep-size-mirror';
  const LEGACY_SIZE_POPUP_SELECTOR = '.size-selector-popup[data-size-popup]';
  const LEGACY_SIZE_OPTIONS_SELECTOR = '[data-size-options]';
  const LEGACY_SIZE_OPTION_SELECTOR = '.radio__button';
  const LEGACY_POPUP_MAX_WIDTH = 1023;

  if (!customElements.get('product-sticky-bar')) {
    customElements.define(
      'product-sticky-bar',
      class ProductStickyBar extends HTMLElement {
        connectedCallback() {
          this.strip = this.querySelector('[data-sticky-strip]');
          this.popup = this.querySelector('[data-sticky-size-popup]');
          this.openButton = this.querySelector('[data-sticky-select-size]');
          this.addButton = this.querySelector('[data-size-popup-add]');
          this.closeButtons = Array.from(this.querySelectorAll('[data-size-popup-close]'));
          this.sizeButtons = Array.from(this.querySelectorAll('[data-size-option-value]'));
          this.sizeOptionName = this.dataset.sizeOptionName || '';
          this.selectedValue = null;
          this.lastFocused = null;

          if (!this.strip || !this.popup || !this.openButton) return;

          this.handleOpen = this.handleOpen.bind(this);
          this.handleClose = this.handleClose.bind(this);
          this.handleKeydown = this.handleKeydown.bind(this);
          this.handleSizeClick = this.handleSizeClick.bind(this);
          this.handleAdd = this.handleAdd.bind(this);

          this.openButton.addEventListener('click', this.handleOpen);
          this.closeButtons.forEach((button) => button.addEventListener('click', this.handleClose));
          this.sizeButtons.forEach((button) => button.addEventListener('click', this.handleSizeClick));
          if (this.addButton) this.addButton.addEventListener('click', this.handleAdd);
          document.addEventListener('keydown', this.handleKeydown);

          this.watchScrollTarget();
        }

        disconnectedCallback() {
          if (this.openButton) this.openButton.removeEventListener('click', this.handleOpen);
          this.closeButtons.forEach((button) => button.removeEventListener('click', this.handleClose));
          this.sizeButtons.forEach((button) => button.removeEventListener('click', this.handleSizeClick));
          if (this.addButton) this.addButton.removeEventListener('click', this.handleAdd);
          document.removeEventListener('keydown', this.handleKeydown);

          if (this.observer) this.observer.disconnect();
          document.body.classList.remove(BODY_OPEN_CLASS);
        }

        /* = visibility = */

        watchScrollTarget() {
          let target = null;
          for (const selector of SCROLL_TARGET_SELECTORS) {
            target = document.querySelector(selector);
            if (target) break;
          }
          if (!target) return;

          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                /* Only once the target has been scrolled UP and out of view.
                   Without the boundingClientRect check the bar would also show on
                   first paint, while the form is still below the fold. */
                const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
                this.strip.classList.toggle('is-visible', scrolledPast);
              });
            },
            { threshold: 0 }
          );

          this.observer.observe(target);
        }

        /* = popup = */

        handleOpen(event) {
          event.preventDefault();

          if (this.openLegacySizePopup()) return;

          this.lastFocused = document.activeElement;
          this.popup.hidden = false;
          this.openButton.setAttribute('aria-expanded', 'true');
          document.body.classList.add(BODY_OPEN_CLASS);

          this.syncSizeGuide();

          const firstEnabled = this.sizeButtons.find((button) => !button.disabled);
          if (firstEnabled) firstEnabled.focus();
        }

        openLegacySizePopup() {
          if (window.innerWidth > LEGACY_POPUP_MAX_WIDTH) return false;

          const legacyPopup = document.querySelector(LEGACY_SIZE_POPUP_SELECTOR);
          if (!legacyPopup || !legacyPopup.querySelector(LEGACY_SIZE_OPTIONS_SELECTOR)) return false;

          const form = document.querySelector(PRODUCT_FORM_SELECTOR);
          if (!form || form.querySelectorAll(LEGACY_SIZE_OPTION_SELECTOR).length === 0) return false;

          const addToCart = form.querySelector(ADD_TO_CART_SELECTOR);
          if (!addToCart || addToCart.disabled) return false;

          addToCart.click();
          return true;
        }

        syncSizeGuide() {
          const mirror = this.popup.querySelector(SIZE_GUIDE_MIRROR_SELECTOR);
          if (mirror && typeof mirror.sync === 'function') mirror.sync();
        }

        handleClose(event) {
          if (event) event.preventDefault();
          this.close();
        }

        close() {
          this.popup.hidden = true;
          this.openButton.setAttribute('aria-expanded', 'false');
          document.body.classList.remove(BODY_OPEN_CLASS);

          if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
            this.lastFocused.focus();
          }
        }

        handleKeydown(event) {
          if (event.key !== 'Escape' || this.popup.hidden) return;
          this.close();
        }

        /* = size selection = */

        handleSizeClick(event) {
          const button = event.currentTarget;
          if (button.disabled) return;

          this.selectedValue = button.dataset.sizeOptionValue;

          this.sizeButtons.forEach((sizeButton) => {
            const isSelected = sizeButton === button;
            sizeButton.classList.toggle('is-selected', isSelected);
            sizeButton.setAttribute('aria-pressed', String(isSelected));
          });

          /* Drive the real form now rather than at add time, so the theme's own
             variant logic updates the price shown in the bar as well. */
          const form = document.querySelector(PRODUCT_FORM_SELECTOR);
          if (form) this.applySizeToForm(form, this.selectedValue);

          if (this.addButton) this.addButton.disabled = false;
        }

        applySizeToForm(form, value) {
          const selectors = form.querySelectorAll(OPTION_SELECTOR);

          for (const element of selectors) {
            if (element.type === 'radio') {
              if (element.value === value) {
                if (!element.checked) element.click();
                return true;
              }
              continue;
            }

            /* Popout-style selectors are hidden inputs named options[Taille]. */
            const name = element.getAttribute('name') || '';
            if (this.sizeOptionName && name.indexOf(this.sizeOptionName) !== -1) {
              element.value = value;
              element.dispatchEvent(new Event('change', { bubbles: true }));
              return true;
            }
          }

          return false;
        }

        /* = add to cart, via the real form = */

        handleAdd(event) {
          event.preventDefault();
          if (!this.selectedValue) return;

          const form = document.querySelector(PRODUCT_FORM_SELECTOR);
          const addToCart = form ? form.querySelector(ADD_TO_CART_SELECTOR) : null;

          if (!form || !addToCart || !this.applySizeToForm(form, this.selectedValue)) {
            this.sendShopperToForm(form);
            return;
          }

          this.close();
          addToCart.click();
        }

        sendShopperToForm(form) {
          this.close();
          if (form && typeof form.scrollIntoView === 'function') {
            form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    );
  }
})();
